import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { HavePaginationEntity } from 'src/entities/HavePagination.entity';
import { PartnerCompanyEntity, PartnerCompanyMastersEntity, PartnerCompanyRequestEntity } from 'src/entities/PartnerCompanyEntity';
import requestUtil from 'src/utils/request.util';

const requestPathBase = '/api/v4/master/partner_companies';
export const queryKey = 'usePartnerCompaniesRequest';

const getResources = async (page: number, sortCondition: string, searchCondition: string, per?: number) => {
  const requestParams = [
    `page=${page + 1}`,
    sortCondition ? `sort_condition=${sortCondition}` : '',
    searchCondition ? `${searchCondition}` : '',
    per ? `per=${per}` : '',
  ].filter((maybe) => maybe).join('&');

  const requestPath = [
    requestPathBase,
    requestParams,
  ].join('?');

  const response: AxiosResponse<HavePaginationEntity<PartnerCompanyEntity>> = await axios.get<HavePaginationEntity<PartnerCompanyEntity>>(requestPath);

  return response.data;
};

export const usePartnerCompaniesRequest = (page: number, sortCondition: string, searchCondition: string, enabled: boolean, per?: number,) => useQuery({
  queryKey: [queryKey, { page, sortCondition, searchCondition, per }],
  queryFn: () => getResources(page, sortCondition, searchCondition, per),
  staleTime: Infinity,
  enabled
});

const mastersPathBase = '/api/v2/partner_companies/masters';

const getMasters = async (companyId?: number) => {
  const requestPath = companyId ? `${mastersPathBase}?company_id=${companyId}` : mastersPathBase;
  const response: AxiosResponse<PartnerCompanyMastersEntity> = await axios.get<PartnerCompanyMastersEntity>(requestPath);
  return response.data;
};

/**
 * 協力会社フォームの選択肢マスタ。#329
 *
 * @param companyId 選択肢を引く会社。**編集する協力会社自身の会社**（フォームの事業所）を渡す。
 *   車両タイプの選択肢は会社ごとのマスタなので、選択中の会社で引くと、全社表示で他社の行を
 *   開いたときにその会社にしか無い値が選択肢に出ない。現在値は withCurrentValues が残すので
 *   データは消えないが、一度外すと選び直せず、他社にしか無い値を選ぶと保存が 400 になる。
 *   省略・0 のときはログイン会社ぶん（従来どおり）。
 *
 *   Why not 0 も送る: 0 は「事業所をまだ選んでいない」状態の値で、会社 ID ではない。
 *   送ると API の selectable_company_ids 検証に落ちて 403 になる。
 *   Why not all_visible で表示できる会社ぶんをまとめて返す: 会社 ID を渡す仕組みと二重になり、
 *   どちらが効いているか分からなくなる。まとめて返しても「どの会社の値か」は区別できない。
 */
export const usePartnerCompanyMastersRequest = (companyId?: number) => useQuery({
  // 会社ごとにキャッシュを分ける。同じキーに相乗りすると先に引いた方の選択肢が使い回される。
  // 0 と省略はどちらもログイン会社ぶんなので同じキーへ寄せる
  queryKey: [queryKey, 'master', companyId || null],
  queryFn: () => getMasters(companyId),
  staleTime: Infinity
});

export const useMutationPartnerCompanies = () => {
  const queryClient = useQueryClient();
  return {
    addPartnerCompany: useMutation({
      mutationFn: (entity: PartnerCompanyRequestEntity) => axios.post('/api/v2/partner_companies', requestUtil.convertKeysToSnakeCase(entity)),

      onSuccess: () => queryClient.invalidateQueries({
        queryKey: ['usePartnerCompaniesRequest']
      })
    }),
    updatePartnerCompany: useMutation({
      mutationFn: (entity: PartnerCompanyEntity) => axios.put(`/api/v2/partner_companies/${entity.id}`, requestUtil.convertKeysToSnakeCase(entity)),

      onSuccess: () => queryClient.invalidateQueries({
        queryKey: ['usePartnerCompaniesRequest']
      })
    }),
    deletePartnerCompanies: useMutation({
      mutationFn: (ids: number[]) => axios.delete('/api/v2/partner_companies/bulk', { data: { ids } }),

      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: ['usePartnerCompaniesRequest']
        });
      }
    })
  };
};
